// IciciPaymentDialog.js
//
// Both ICICI tenders, in one component:
//
//   tender="qr"      generateQR    — a UPI QR the customer scans at the counter
//   tender="hosted"  initiateSale  — ICICI's own payment page (card, netbanking,
//                                    wallet, UPI) opened in a popup window
//
// They share a component because they share the part that matters: neither
// one can tell this screen what happened. A UPI QR is paid out of band — the
// customer's phone, their app, their bank — and the hosted page runs on
// icicibank.com, cross-origin, so nothing in it can call back into the POS.
// In both cases the only way to learn the outcome is to ask the gateway
// repeatedly. So the create call and the "waiting" body differ; the poll
// loop, the timeout, the cancel and all four outcomes are identical.
//
// Phases:
//   creating    — the create call is still in flight
//   awaiting    — QR on screen / payment window open, polling for the outcome
//   paid        — gateway confirmed it; the payment has been recorded
//   failed      — gateway said REJ, i.e. definitely not paid
//   unconfirmed — the poll window ran out with the payment still Initiated
//   error       — couldn't start the payment at all
//
// "unconfirmed" is deliberately NOT "failed". ICICI's txnStatus REQ means
// "received, still in process", so a timeout means we don't know yet — not
// that the money didn't move. Showing "failed" there invites the cashier to
// take cash from someone whose payment lands a moment later, and the customer
// pays twice. Payment Advice (the server-side webhook) settles those.

import React, { Component } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';

import iciciAxios from './icici-axios';
import { loadQrEncoder, renderQrInto } from './iciciQr';

const TENDERS = {
    qr: {
        title:    'ICICI UPI Payment',
        endpoint: '/api/icici/qr',
        // Roughly the outer edge of someone completing a scan at the counter.
        timeoutSeconds: 120,
    },
    hosted: {
        title:    'ICICI Card / Netbanking',
        endpoint: '/api/icici/sale',
        // Longer than the QR on purpose: the hosted page means typing card
        // details and clearing a bank OTP or 3DS step, and the OTP alone can
        // eat a minute. Timing out at 120s here would call good payments
        // unconfirmed for no reason.
        timeoutSeconds: 300,
    },
};

// 3s matches UPI's real settlement latency (typically 5-20s end to end).
// Polling faster just hammers the API for an answer that can't have changed
// and risks rate limiting; slower leaves the cashier staring at a screen
// that already knew.
const POLL_SECONDS = 3;

const QR_PIXELS = 220;

const POPUP_NAME     = 'icici-payment';
const POPUP_FEATURES = 'width=520,height=740,menubar=no,toolbar=no,location=yes,status=no';

const fmtMoney = (n) => (Number(n) || 0).toFixed(2);

const fmtClock = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
};

const readError = (err) =>
    err?.response?.data?.error || err?.message || 'Could not reach the payment service';

class IciciPaymentDialog extends Component {

    state = {
        phase:         'creating',
        merchantRef:   null,
        upiQr:         null,       // qr tender
        paymentUrl:    null,       // hosted tender
        txnId:         null,
        paymentMode:   null,
        secondsLeft:   0,
        error:         null,
        qrUnavailable: false,
        popupBlocked:  false,
    };

    qrHost = React.createRef();

    _mounted = false;
    _timer   = null;
    _tick    = 0;
    _polling = false;
    _popup   = null;

    // ─────────────────────────────────────────────────────────────────────
    // LIFECYCLE
    // ─────────────────────────────────────────────────────────────────────

    componentDidMount() {
        this._mounted = true;
        // The caller may have opened a blank popup inside the click handler
        // (see handleIciciPay in BillingScreen) — adopt it before starting.
        this._popup = this.props.popupWindow || null;
        this.start();
    }

    componentWillUnmount() {
        this._mounted = false;
        this.stopTimer();
        this.closePopup();
    }

    // The poll and countdown both outlive individual renders, so every
    // setState after an await goes through here rather than risking one
    // landing after the dialog has closed.
    safeSetState = (patch, done) => {
        if (this._mounted) this.setState(patch, done);
    };

    config = () => TENDERS[this.props.tender] || TENDERS.qr;

    isHosted = () => this.props.tender === 'hosted';

    // ─────────────────────────────────────────────────────────────────────
    // GATEWAY CALLS
    // ─────────────────────────────────────────────────────────────────────

    start = () => {
        const { amount, orderId, customerName, customerMobile } = this.props;
        const { endpoint, timeoutSeconds } = this.config();

        // Cleared rather than merged, so a "Try Again" after a failure can't
        // leave a stale merchantRef behind for the new poll loop to chase.
        this.setState({
            phase:         'creating',
            merchantRef:   null,
            upiQr:         null,
            paymentUrl:    null,
            txnId:         null,
            paymentMode:   null,
            error:         null,
            qrUnavailable: false,
            popupBlocked:  false,
            secondsLeft:   timeoutSeconds,
        });

        const payload = {
            orderId,
            // Amount goes as a Number — the service formats it for ICICI with
            // invariant culture, and a locale-formatted string would both fail
            // validation and corrupt the request hash.
            amount:         Number(amount),
            customerName:   customerName   || undefined,
            customerMobile: customerMobile || undefined,
            // paymentMode left unset on purpose: null lets the customer pick
            // card / netbanking / wallet / UPI on ICICI's page. Set it only to
            // deliberately restrict the page to one instrument.
        };

        iciciAxios.post(endpoint, payload)
            .then(({ data }) => {
                if (!data || !data.success || !data.merchantRef) {
                    throw new Error((data && data.error) || 'The gateway did not accept the payment');
                }
                return this.isHosted() ? this.beginHosted(data) : this.beginQr(data);
            })
            .catch((err) => {
                this.closePopup();
                this.safeSetState({ phase: 'error', error: readError(err) });
            });
    };

    beginQr = (data) => {
        if (!data.upiQr) throw new Error('The gateway did not return a QR');

        this.safeSetState({
            phase:       'awaiting',
            merchantRef: data.merchantRef,
            upiQr:       data.upiQr,
        }, () => {
            // The ref div only exists once phase is 'awaiting', so drawing
            // waits for this commit.
            loadQrEncoder().then(
                this.drawQr,
                () => this.safeSetState({ qrUnavailable: true }),
            );
            this.startTimer();
        });
    };

    beginHosted = (data) => {
        if (!data.paymentUrl) throw new Error('The gateway did not return a payment URL');

        // Point the already-open blank popup at ICICI. Navigating a window we
        // opened ourselves is allowed; reading anything back from it once it
        // is on icicibank.com is not, which is exactly why the outcome comes
        // from polling rather than from watching the window.
        let blocked = true;
        try {
            if (this._popup && !this._popup.closed) {
                this._popup.location.href = data.paymentUrl;
                blocked = false;
            }
        } catch (e) {
            blocked = true;
        }

        this.safeSetState({
            phase:        'awaiting',
            merchantRef:  data.merchantRef,
            paymentUrl:   data.paymentUrl,
            popupBlocked: blocked,
        }, this.startTimer);
    };

    poll = () => {
        const { merchantRef } = this.state;

        // Skip if the previous poll is still in flight, so a slow response
        // can't stack requests on top of each other.
        if (this._polling || !merchantRef) return;
        this._polling = true;

        iciciAxios.get(`/api/icici/payments/${encodeURIComponent(merchantRef)}`)
            .then(({ data }) => {
                if (!this._mounted || !data) return;

                if (data.isPaid) {
                    this.settleAsPaid(data);
                } else if (data.state === 'Failed' || data.state === 'Cancelled') {
                    this.stopTimer();
                    this.closePopup();
                    this.safeSetState({ phase: 'failed' });
                }
                // Anything else (Initiated) means not yet — keep polling.
                // Note this includes the customer closing the payment window:
                // they may well have paid and then closed it, so a vanished
                // popup is not an outcome either.
            })
            .catch(() => {
                // One failed poll is not an outcome — the network may have
                // blipped, or the service may be restarting. Keep polling
                // until the window closes rather than calling it a failure.
            })
            .then(() => { this._polling = false; });
    };

    // Cancels the attempt at the gateway so it can't be paid later. This
    // matters more than it looks for the QR: ICICI returned expiry=1440
    // (minutes) in UAT, so an abandoned QR stays payable for a full day —
    // long enough for a customer to find it in their app history tomorrow
    // and pay a bill that was already closed as cash.
    cancelAtGateway = () => {
        const { merchantRef } = this.state;
        if (!merchantRef) return;

        iciciAxios
            .post(`/api/icici/payments/${encodeURIComponent(merchantRef)}/cancel`)
            .catch(() => {
                // Best effort. Payment Advice still backstops a late payment,
                // and there's nothing useful the cashier could do about a
                // failed cancel anyway.
            });
    };

    // ─────────────────────────────────────────────────────────────────────
    // PAYMENT WINDOW (hosted tender only)
    // ─────────────────────────────────────────────────────────────────────

    // Called from a button click, so this window.open is a genuine user
    // gesture and won't be blocked — unlike one fired from the create call's
    // .then(), which is why the caller pre-opens a blank window instead.
    openPaymentWindow = () => {
        const { paymentUrl } = this.state;
        if (!paymentUrl) return;

        this._popup = window.open(paymentUrl, POPUP_NAME, POPUP_FEATURES);
        this.safeSetState({ popupBlocked: !this._popup });
    };

    closePopup = () => {
        try {
            if (this._popup && !this._popup.closed) this._popup.close();
        } catch (e) {
            // Cross-origin inspection can throw once the window is on
            // icicibank.com. Nothing to do — the customer can close it.
        }
        this._popup = null;
    };

    // ─────────────────────────────────────────────────────────────────────
    // TIMER — one 1s interval drives both the countdown and the poll, so
    // there is only ever a single handle to clear.
    // ─────────────────────────────────────────────────────────────────────

    startTimer = () => {
        this.stopTimer();
        this._tick = 0;
        this._timer = setInterval(this.onTick, 1000);
    };

    stopTimer = () => {
        if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
        }
    };

    onTick = () => {
        const { timeoutSeconds } = this.config();

        this._tick += 1;
        const secondsLeft = Math.max(0, timeoutSeconds - this._tick);
        this.safeSetState({ secondsLeft });

        if (secondsLeft === 0) {
            this.handleTimeout();
            return;
        }
        if (this._tick % POLL_SECONDS === 0) this.poll();
    };

    // ─────────────────────────────────────────────────────────────────────
    // OUTCOMES
    // ─────────────────────────────────────────────────────────────────────

    settleAsPaid = (record) => {
        this.stopTimer();
        this.closePopup();
        this.safeSetState({
            phase:       'paid',
            txnId:       record.txnId,
            paymentMode: record.paymentMode,
        });

        // Records against the amount we asked for, not anything echoed back,
        // so the row in Payment History always matches what was charged.
        this.props.onPaid({
            amount:        Number(this.props.amount),
            transactionNo: record.txnId || this.state.merchantRef,
            paymentMode:   record.paymentMode,
        });
    };

    handleTimeout = () => {
        this.stopTimer();
        this.cancelAtGateway();
        this.closePopup();
        this.safeSetState({ phase: 'unconfirmed' });
    };

    // Closing while the attempt is still live means the cashier gave up, so
    // it gets cancelled on the way out for the same reason as a timeout.
    handleHide = () => {
        const { phase } = this.state;
        if (phase === 'awaiting' || phase === 'creating') this.cancelAtGateway();

        this.stopTimer();
        this.closePopup();
        this.props.onHide();
    };

    // ─────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────

    drawQr = () => {
        if (!renderQrInto(this.qrHost.current, this.state.upiQr, QR_PIXELS)) {
            this.safeSetState({ qrUnavailable: true });
        }
    };

    renderCreating = () => (
        <div className="p-text-center p-py-5">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2196F3' }} />
            <div className="p-mt-3 p-text-secondary">
                {this.isHosted() ? 'Opening payment page…' : 'Generating QR…'}
            </div>
        </div>
    );

    renderAmount = () => (
        <div className="p-text-center" style={{ fontSize: '1.6rem', fontWeight: 700 }}>
            ₹ {fmtMoney(this.props.amount)}
        </div>
    );

    renderWaitBar = () => (
        <div
            className="p-d-flex p-jc-between p-ai-center p-px-3 p-py-2 p-border-round"
            style={{ background: '#e3f2fd' }}
        >
            <span className="p-d-flex p-ai-center" style={{ gap: '0.5rem' }}>
                <i className="pi pi-spin pi-spinner" style={{ color: '#2196F3' }} />
                <span style={{ fontWeight: 600 }}>Waiting for payment…</span>
            </span>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                {fmtClock(this.state.secondsLeft)}
            </span>
        </div>
    );

    renderRef = () => (
        this.state.merchantRef ? (
            <div className="p-text-center p-text-secondary p-mt-2" style={{ fontSize: '0.7rem' }}>
                Ref {this.state.merchantRef}
            </div>
        ) : null
    );

    renderAwaitingQr = () => {
        const { upiQr, qrUnavailable } = this.state;

        return (
            <div>
                {this.renderAmount()}
                <div className="p-text-center p-text-secondary p-mb-3" style={{ fontSize: '0.8rem' }}>
                    Ask the customer to scan with any UPI app
                </div>

                <div className="p-d-flex p-jc-center p-mb-3">
                    <div
                        ref={this.qrHost}
                        style={{
                            width:        QR_PIXELS,
                            height:       QR_PIXELS,
                            border:       '1px solid #dee2e6',
                            padding:      8,
                            background:   '#fff',
                            borderRadius: 4,
                        }}
                    />
                </div>

                {/* Encoder didn't load — show the intent string so the payment
                    isn't dead in the water. Selectable rather than
                    copy-to-clipboard: navigator.clipboard is unavailable on the
                    plain-http LAN origin a till may well be served from. */}
                {qrUnavailable && (
                    <div
                        className="p-mb-3 p-p-2"
                        style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4 }}
                    >
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: 4 }}>
                            <i className="pi pi-exclamation-triangle p-mr-1" style={{ color: '#ff9800' }} />
                            QR image unavailable — use this UPI link
                        </div>
                        <code style={{ fontSize: '0.7rem', wordBreak: 'break-all', userSelect: 'all', display: 'block' }}>
                            {upiQr}
                        </code>
                    </div>
                )}

                {this.renderWaitBar()}
                {this.renderRef()}
            </div>
        );
    };

    renderAwaitingHosted = () => {
        const { popupBlocked } = this.state;

        return (
            <div>
                {this.renderAmount()}
                <div className="p-text-center p-text-secondary p-mb-3" style={{ fontSize: '0.8rem' }}>
                    Card, netbanking, wallet or UPI on ICICI's secure page
                </div>

                {popupBlocked ? (
                    <div
                        className="p-mb-3 p-p-3 p-text-center"
                        style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4 }}
                    >
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 8 }}>
                            <i className="pi pi-exclamation-triangle p-mr-1" style={{ color: '#ff9800' }} />
                            The browser blocked the payment window
                        </div>
                        <Button
                            label="Open Payment Window"
                            icon="pi pi-external-link"
                            className="p-button-primary"
                            onClick={this.openPaymentWindow}
                        />
                    </div>
                ) : (
                    <div className="p-mb-3 p-p-3 p-text-center" style={{ background: '#f8f9fa', borderRadius: 4 }}>
                        <div className="p-text-secondary p-mb-2" style={{ fontSize: '0.8rem' }}>
                            The payment window is open. Ask the customer to complete it there —
                            this screen updates on its own.
                        </div>
                        {/* The customer can close or lose the window mid-payment;
                            reopening resumes the same attempt, since the URL and
                            the merchantRef are unchanged. */}
                        <Button
                            label="Reopen Window"
                            icon="pi pi-external-link"
                            className="p-button-text p-button-sm"
                            onClick={this.openPaymentWindow}
                        />
                    </div>
                )}

                {this.renderWaitBar()}
                {this.renderRef()}
            </div>
        );
    };

    renderPaid = () => {
        const { txnId, merchantRef, paymentMode } = this.state;

        return (
            <div className="p-text-center p-py-4">
                <i className="pi pi-check-circle" style={{ fontSize: '2.75rem', color: '#4caf50' }} />
                <div className="p-mt-2" style={{ fontSize: '1.4rem', fontWeight: 700 }}>
                    ₹ {fmtMoney(this.props.amount)} received
                </div>
                <div className="p-text-secondary p-mt-1" style={{ fontSize: '0.78rem' }}>
                    {paymentMode ? `${paymentMode} · ` : ''}{txnId || merchantRef}
                </div>
            </div>
        );
    };

    renderFailed = () => (
        <div className="p-text-center p-py-4">
            <i className="pi pi-times-circle" style={{ fontSize: '2.75rem', color: '#f44336' }} />
            <div className="p-mt-2" style={{ fontSize: '1.2rem', fontWeight: 700 }}>
                Payment declined
            </div>
            <div className="p-text-secondary p-mt-1" style={{ fontSize: '0.8rem' }}>
                The gateway rejected it. Nothing was charged — take another payment.
            </div>
        </div>
    );

    // Worded to stop the cashier reading a timeout as a failure. See the file
    // header: the money may well have moved.
    renderUnconfirmed = () => {
        const minutes = Math.round(this.config().timeoutSeconds / 60);

        return (
            <div className="p-text-center p-py-3">
                <i className="pi pi-exclamation-triangle" style={{ fontSize: '2.5rem', color: '#ff9800' }} />
                <div className="p-mt-2" style={{ fontSize: '1.2rem', fontWeight: 700 }}>
                    Not confirmed
                </div>
                <div className="p-text-secondary p-mt-2" style={{ fontSize: '0.8rem', lineHeight: 1.45 }}>
                    No confirmation arrived within {minutes} {minutes === 1 ? 'minute' : 'minutes'}, and the
                    attempt has been cancelled.
                    <br />
                    <b>This does not mean the payment failed.</b> If the customer's app or bank shows it as
                    successful, check the reference below before taking another payment.
                </div>
                {this.state.merchantRef && (
                    <div className="p-mt-2" style={{ fontSize: '0.72rem', fontWeight: 600 }}>
                        Ref {this.state.merchantRef}
                    </div>
                )}
            </div>
        );
    };

    renderError = () => (
        <div className="p-text-center p-py-4">
            <i className="pi pi-times-circle" style={{ fontSize: '2.5rem', color: '#f44336' }} />
            <div className="p-mt-2" style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                Couldn't start the payment
            </div>
            <div className="p-text-secondary p-mt-1" style={{ fontSize: '0.8rem' }}>
                {this.state.error}
            </div>
        </div>
    );

    renderBody = () => {
        switch (this.state.phase) {
            case 'creating':    return this.renderCreating();
            case 'awaiting':    return this.isHosted() ? this.renderAwaitingHosted() : this.renderAwaitingQr();
            case 'paid':        return this.renderPaid();
            case 'failed':      return this.renderFailed();
            case 'unconfirmed': return this.renderUnconfirmed();
            default:            return this.renderError();
        }
    };

    renderFooter = () => {
        const { phase } = this.state;

        if (phase === 'creating') return null;

        if (phase === 'awaiting') {
            return (
                <div className="p-d-flex p-jc-end">
                    <Button
                        label="Cancel Payment"
                        className="p-button-outlined p-button-danger"
                        onClick={this.handleHide}
                    />
                </div>
            );
        }

        if (phase === 'paid') {
            return (
                <div className="p-d-flex p-jc-end">
                    <Button
                        label="Done"
                        icon="pi pi-check"
                        className="p-button-success"
                        style={{ minWidth: '120px' }}
                        onClick={this.handleHide}
                        autoFocus
                    />
                </div>
            );
        }

        // failed / unconfirmed / error — all recoverable by starting over.
        return (
            <div className="p-d-flex p-jc-end" style={{ gap: '0.5rem' }}>
                <Button label="Close" className="p-button-outlined" onClick={this.handleHide} />
                <Button
                    label="Try Again"
                    icon="pi pi-refresh"
                    className="p-button-primary"
                    onClick={this.start}
                />
            </div>
        );
    };

    render() {
        return (
            <Dialog
                header={this.config().title}
                visible
                modal
                // No dismissableMask and not closable mid-creation: an
                // accidental click outside shouldn't abandon a live payment,
                // and there's no ref to cancel with until the call comes back.
                closable={this.state.phase !== 'creating'}
                onHide={this.handleHide}
                style={{ width: '380px' }}
                footer={this.renderFooter()}
            >
                {this.renderBody()}
            </Dialog>
        );
    }
}

export default IciciPaymentDialog;
